import { Injectable, inject, signal, Signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, of, tap, timer, throwError } from 'rxjs';
import { map, retry, switchMap } from 'rxjs/operators';

import type {
  AuthUser,
  AuthSuccessResponse,
  LoginResponse,
  GenericMessageResponse,
  RegisterPersonalDto,
  RegisterCorporateDto,
  Verify2faResponse,
  Verify2faSessionResponse,
} from '@app/core/models';
import type { ApiResponse } from '@app/core/types';
import { environment } from '@env/environment';
import { CrossPlatformOnboardingStateService } from '@app/core/services/cross-platform-onboarding-state.service';
import { validateCrossPlatformPrefill } from '@app/core/validators';

/**
 * 2026-06-08: routes where the user is — by definition — NOT logged in,
 * so the bootstrap /auth/me call should be skipped. Visiting one of these
 * from a fresh tab (e.g. via an invite email) should NOT trigger a 401
 * that bounces the user off the page.
 *
 * Mirrors the PUBLIC_AUTH_ENDPOINTS list in error.interceptor.ts.
 */
const PUBLIC_AUTH_PATHS: readonly string[] = [
  '/auth/login',
  '/auth/verify-2fa',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/setup-password',
];

function isPublicAuthPath(pathname: string): boolean {
  return PUBLIC_AUTH_PATHS.some((p) => pathname.startsWith(p));
}

/**
 * Cookie-based AuthService (CW-13d-client §3.2).
 *
 * Session state lives entirely in-memory via the `currentUser` signal. The
 * actual session credentials (`tb_access` 15-min JWT + `tb_refresh` 30-day
 * sliding token) are HttpOnly cookies set by the backend on
 * `/auth/login` / `/auth/verify-2fa` and cleared on `/auth/logout`. The
 * client MUST NOT read, write, or persist these tokens — `AuthInterceptor`
 * sets `withCredentials: true` so the browser handles them automatically.
 *
 * Response envelope: NestJS's global `ApiResponseInterceptor` wraps every
 * response in `{ success, data, timestamp }`. The service unwraps `data`
 * before exposing it to callers — outwardly the observable streams emit
 * the discriminated union `LoginResponse`, etc.
 *
 * Backwards-incompatible removals from the prior implementation:
 *   - Browser-storage token persistence (was via the legacy TOKEN_KEY)
 *   - setToken() / getToken() public methods
 *
 * Page-refresh handling: `restoreSession()` runs on construction (deferred
 * one microtask to avoid circular DI with HttpClient interceptors) and
 * calls `GET /auth/me`. A 401 means cookies are absent/expired — the
 * `currentUser` signal stays null and `AuthGuard` will redirect.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly cpState = inject(CrossPlatformOnboardingStateService);

  private readonly _currentUser = signal<AuthUser | null>(null);
  private readonly _restoreInProgress = signal<boolean>(false);
  // Tracks whether the bootstrap-time `restoreSession()` has settled.
  // Bootstrap failure must NOT wipe `_currentUser` because a parallel
  // `login()` may have populated it (see error handler below); subsequent
  // manual refreshes SHOULD wipe on 401 (session expired mid-use).
  private hasCompletedFirstRestore = false;

  readonly currentUser: Signal<AuthUser | null> = this._currentUser.asReadonly();
  readonly restoreInProgress: Signal<boolean> = this._restoreInProgress.asReadonly();

  /**
   * Convenience computed — true when an AuthUser is loaded into memory.
   * Equivalent to `isAuthenticated()` but reactive (usable in templates
   * via `signal()` access).
   */
  readonly isAuthenticatedSignal = computed(() => this._currentUser() !== null);

  constructor() {
    // 2026-06-08: skip the bootstrap /auth/me call when the user has
    // landed on a public auth page (login, verify-2fa, forgot/reset/
    // setup-password). They have no session there by definition — the
    // call would 401, the error interceptor would clearSession() and
    // bounce them away from the page they were trying to reach. Most
    // visible on sub-user invite links to /auth/setup-password.
    //
    // Inspecting `window.location.pathname` is safe here: Router hasn't
    // resolved the initial navigation yet, but the browser URL is the
    // authoritative source for the route the user is opening.
    if (isPublicAuthPath(window.location.pathname)) {
      this.hasCompletedFirstRestore = true;
      return;
    }

    // Set the in-progress flag SYNCHRONOUSLY so the AuthGuard (which runs in
    // the same tick as initial route resolution) sees a session restore is
    // pending and waits, instead of bouncing the user to login. The actual
    // HTTP call is still deferred one microtask — AuthService is constructed
    // before HttpClient interceptors finish their own DI graph, so calling
    // http.get() synchronously can trigger a circular dependency through
    // AuthInterceptor.
    this._restoreInProgress.set(true);
    Promise.resolve().then(() => this.restoreSession());
  }

  // ---------------------------------------------------------------- //
  //  Session restoration                                              //
  // ---------------------------------------------------------------- //

  /**
   * Restore the in-memory user via `GET /auth/me` using the cookie session.
   * Public so that callers (e.g. a manual `Refresh user` flow) can trigger
   * it; in normal operation it runs once on construction.
   */
  restoreSession(): void {
    // Capture whether this is the bootstrap restore BEFORE marking it
    // complete — the flag flips inside both subscribe callbacks.
    const isFirstRestore = !this.hasCompletedFirstRestore;
    this._restoreInProgress.set(true);
    this.http
      .get<ApiResponse<AuthUser>>(`${environment.apiUrl}/auth/me`)
      .pipe(
        // Retry on transient errors (429 throttle, 5xx) up to 2 times with
        // backoff. A genuine 401 (no/expired session) fails fast — those
        // bounce the user to login. Without this retry, a rate-limit burst
        // during page bootstrap drops the session and logs the user out
        // on refresh even though the cookie is valid.
        retry({
          count: 2,
          delay: (err: HttpErrorResponse, attempt) => {
            const transient =
              err.status === 0 ||
              err.status === 429 ||
              (err.status >= 500 && err.status < 600);
            if (!transient) return throwError(() => err);
            return timer(300 * Math.pow(2, attempt - 1));
          },
        }),
        map((res) => res.data),
      )
      .subscribe({
        next: (user) => {
          this._currentUser.set(user);
          this.hasCompletedFirstRestore = true;
          this._restoreInProgress.set(false);
        },
        error: () => {
          // 401 (or persistent transient failure). Do NOT navigate here —
          // AuthGuard handles the redirect.
          //
          // CRITICAL: on the bootstrap restore, do NOT wipe `_currentUser`.
          // The microtask scheduled in the constructor races against a
          // user's submit on the login page; `login()` may have populated
          // `_currentUser` mid-flight. Wiping it here would cause AuthGuard
          // to bounce the post-login `/dashboard` navigation back to
          // `/auth/login` (login succeeds → no apparent redirect).
          //
          // On subsequent manual refreshes (caller invokes
          // `restoreSession()` again), the user was already authenticated,
          // so a 401 means the session expired mid-use and we clear.
          if (!isFirstRestore) {
            this._currentUser.set(null);
          }
          this.hasCompletedFirstRestore = true;
          this._restoreInProgress.set(false);
        },
      });
  }

  // ---------------------------------------------------------------- //
  //  Login + 2FA                                                      //
  // ---------------------------------------------------------------- //

  /**
   * POST /auth/login. Backend sets `tb_access` + `tb_refresh` cookies on
   * direct success. On 2FA-enabled accounts, returns `{ requires2fa, globalUserId }`
   * WITHOUT setting cookies — the cookies are issued only after a successful
   * `verify2fa` call. Response body (after unwrapping ApiResponse) is either
   * AuthSuccessResponse or TwoFactorRequiredResponse — discriminate on the
   * `requires2fa` key.
   */
  login(email: string, password: string): Observable<LoginResponse> {
    return this.http
      .post<ApiResponse<LoginResponse>>(`${environment.apiUrl}/auth/login`, { email, password })
      .pipe(
        map((res) => res.data),
        tap((data) => {
          if ('success' in data && data.success) {
            this._currentUser.set(data.user);
          }
        }),
      );
  }

  /**
   * POST /auth/verify-2fa. Completes the 2FA challenge — server sets
   * `tb_access` + `tb_refresh` cookies on success. Rate-limited to 5
   * verifications / 15 min / user.
   *
   * CW-13h (LD-1, AD-047) — extended to a 2-branch response:
   *   Branch A — `{ success: true; user }` — existing session flow.
   *     `_currentUser` set; caller navigates to dashboard.
   *   Branch B — `{ requiresOnboarding: true; registeredPlatform; prefill }` —
   *     no session cookies; backend has set `tb_onboarding_token` instead.
   *     The `prefill` block is shape-validated (LD-2 closes SA LOW-2)
   *     and stashed in `CrossPlatformOnboardingStateService`. Caller
   *     dispatches to the consent modal or wizard.
   *
   * On Branch B malformed prefill, the observable errors with a generic
   * message; state is NOT populated. An `ops`-level console.error
   * records the first failing field (no PII) for triage.
   *
   * CW-13h Prompt 5 — refactored to `switchMap` so each emission is
   * fully self-contained: the validator result lives only inside the
   * per-emission pipeline scope. This eliminates the previous
   * `_branchBFailed` instance flag on this singleton service which had
   * a narrow concurrency window between the `tap` (setter) and the
   * downstream `map` (reader). See HIGH-1 in
   * docs/reviews/cw13h-client-code-review.md.
   */
  verify2fa(globalUserId: string, otp: string): Observable<Verify2faResponse> {
    return this.http
      .post<ApiResponse<Verify2faResponse>>(
        `${environment.apiUrl}/auth/verify-2fa`,
        { globalUserId, otp },
      )
      .pipe(
        map((res) => res.data),
        switchMap((data) => {
          if ('requiresOnboarding' in data && data.requiresOnboarding) {
            // Branch B — validate prefill in-pipeline; no shared state.
            const result = validateCrossPlatformPrefill(data.prefill);
            if (!result.ok) {
              // No PII — only field name + reason.
              // eslint-disable-next-line no-console
              console.error(
                '[CW-13h] Malformed verify-2fa prefill',
                { field: result.error.field, reason: result.error.reason },
              );
              return throwError(
                () =>
                  new Error(
                    'Login could not be completed. Please contact support.',
                  ),
              );
            }
            this.cpState.setFromBranchB({
              requiresOnboarding: true,
              registeredPlatform: data.registeredPlatform,
              prefill: result.value,
            });
            return of(data);
          }
          // Branch A — set currentUser and pass the response through.
          if ('success' in data && data.success) {
            this._currentUser.set((data as Verify2faSessionResponse).user);
          }
          return of(data);
        }),
      );
  }

  // ---------------------------------------------------------------- //
  //  Password flows                                                   //
  // ---------------------------------------------------------------- //

  /**
   * POST /auth/forgot-password. Always returns 200 with a generic message
   * regardless of whether the email exists (defense against account
   * enumeration — AD-032).
   */
  forgotPassword(email: string): Observable<GenericMessageResponse> {
    return this.http
      .post<ApiResponse<GenericMessageResponse>>(
        `${environment.apiUrl}/auth/forgot-password`,
        { email },
      )
      .pipe(map((res) => res.data));
  }

  /**
   * POST /auth/reset-password. Token arrives via the email link.
   * Body shape matches the backend `ResetPasswordDto` exactly.
   */
  resetPassword(token: string, newPassword: string): Observable<GenericMessageResponse> {
    return this.http
      .post<ApiResponse<GenericMessageResponse>>(
        `${environment.apiUrl}/auth/reset-password`,
        { token, newPassword },
      )
      .pipe(map((res) => res.data));
  }

  /**
   * POST /auth/setup-password. Sub-user one-time activation from
   * invitation email. The token is single-use; the backend returns 400
   * if it has already been consumed.
   */
  setupPassword(token: string, password: string): Observable<GenericMessageResponse> {
    return this.http
      .post<ApiResponse<GenericMessageResponse>>(
        `${environment.apiUrl}/auth/setup-password`,
        { token, password },
      )
      .pipe(map((res) => res.data));
  }

  // ---------------------------------------------------------------- //
  //  Registration (CW-13e)                                            //
  // ---------------------------------------------------------------- //

  /**
   * POST /auth/register/personal.
   *
   * Body shape matches the backend `RegisterPersonalDto` exactly — see
   * `src/app/core/models/onboarding.model.ts`. The DTO does NOT include
   * `confirmPassword`; `OnboardingStateService.getFinalPayload()` strips
   * it before reaching this method.
   *
   * On 201, the backend sets `tb_access` + `tb_refresh` HttpOnly cookies
   * (same envelope as `login`). The response body (after `ApiResponse`
   * unwrap) is `AuthSuccessResponse` — this method tap-sets
   * `currentUser` so the navigation that follows in the calling
   * component sees an authenticated session.
   *
   * Errors propagate to subscribers — the source-of-funds page maps
   * 409 / 400 / 429 / 5xx to inline error banners (see spec §6.2.4).
   */
  registerPersonal(dto: RegisterPersonalDto): Observable<AuthSuccessResponse> {
    return this.http
      .post<ApiResponse<AuthSuccessResponse>>(
        `${environment.apiUrl}/auth/register/personal`,
        dto,
      )
      .pipe(
        map((res) => res.data),
        tap((data) => {
          if (data.success) {
            this._currentUser.set(data.user);
          }
        }),
      );
  }

  /**
   * POST /auth/register/corporate.
   *
   * Body shape matches the backend `RegisterCorporateDto` exactly — see
   * `src/app/core/models/onboarding.model.ts`. The DTO does NOT include
   * `confirmPassword`; `OnboardingStateService.getFinalPayload()` strips
   * it before reaching this method.
   *
   * On 201, the backend sets `tb_access` + `tb_refresh` HttpOnly cookies
   * (same envelope as `login` / `registerPersonal`). The response body
   * (after `ApiResponse` unwrap) is `AuthSuccessResponse` — this method
   * tap-sets `currentUser` so the navigation that follows in the calling
   * component sees an authenticated session.
   *
   * Errors propagate to subscribers — the source-of-wealth page maps
   * 409 / 400 / 429 / 5xx to inline error banners (see spec §7.3.3).
   */
  registerCorporate(dto: RegisterCorporateDto): Observable<AuthSuccessResponse> {
    return this.http
      .post<ApiResponse<AuthSuccessResponse>>(
        `${environment.apiUrl}/auth/register/corporate`,
        dto,
      )
      .pipe(
        map((res) => res.data),
        tap((data) => {
          if (data.success) {
            this._currentUser.set(data.user);
          }
        }),
      );
  }

  // ---------------------------------------------------------------- //
  //  Logout                                                           //
  // ---------------------------------------------------------------- //

  /**
   * POST /auth/logout. Server clears cookies + revokes the refresh token.
   * Returns 204 No Content. `clearSession()` is called on both success
   * and error (belt-and-suspenders — local state must drop even if the
   * network call fails so the user sees the login page).
   */
  logout(): void {
    this.http.post<void>(`${environment.apiUrl}/auth/logout`, {}).subscribe({
      next: () => this.clearSession(),
      error: () => this.clearSession(),
    });
  }

  /**
   * Clear in-memory session state and navigate to the login page.
   * Called by `ErrorInterceptor` on 401 (after the server has already
   * attempted silent refresh via `tb_refresh`).
   */
  clearSession(): void {
    this._currentUser.set(null);
    this.router.navigate(['/auth/login']);
  }

  // ---------------------------------------------------------------- //
  //  Accessors                                                        //
  // ---------------------------------------------------------------- //

  getUser(): AuthUser | null {
    return this._currentUser();
  }

  setUser(user: AuthUser): void {
    this._currentUser.set(user);
  }

  getUserRole(): string | null {
    return this._currentUser()?.role ?? null;
  }

  /** Discriminator for sub-user (CORPORATE_USER) sessions. */
  isSubUser(): boolean {
    return this._currentUser()?.userType === 'sub_user';
  }

  /**
   * 2026-06-08: granular corporate-permission check used by route guards
   * for sub-user access. The corporate permissions
   * (INITIATE_TRANSFER / INITIATE_DEPOSIT / APPROVE_REQUEST) only gate
   * CORPORATE_USER routes — every other role passes through implicitly:
   *   - CORPORATE_OWNER: full access to every owned-corp route.
   *   - PERSONAL_CLIENT: corporate permissions don't apply; the same
   *     route data is reused under both `/` and `/corporate` paths via
   *     SHARED_FEATURE_ROUTES, so a tag like
   *     `requiredPermission: 'INITIATE_DEPOSIT'` must not block a
   *     personal client visiting `/deposits`.
   *   - CORPORATE_USER: actual lookup in `corporatePermissions`.
   * Unauthenticated callers always return false.
   */
  hasPermission(permission: string): boolean {
    const user = this._currentUser();
    if (!user) return false;
    if (user.role === 'CORPORATE_OWNER') return true;
    if (user.role === 'PERSONAL_CLIENT') return true;
    return (user.corporatePermissions ?? []).includes(permission);
  }

  /**
   * True when KYC is fully complete for this session.
   *   - `userType === 'user'` → `finalKycStatus === true`
   *   - `userType === 'sub_user'` → `subUserState === 'verified'`
   *
   * The derivation for `subUserState` (rejected > verified > pending) is
   * already performed server-side in `AuthService.buildMeResponse()` —
   * the client MUST NOT re-derive it.
   */
  isKycComplete(): boolean {
    const user = this._currentUser();
    if (!user) return false;
    if (user.userType === 'sub_user') return user.subUserState === 'verified';
    return user.finalKycStatus === true;
  }

  isAuthenticated(): boolean {
    return this._currentUser() !== null;
  }
}
